/**
 * Agent Builder Wizard - Create Custom AI Agents
 * Step-by-step guide to building personalized business agents
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Store, ShoppingCart, Briefcase, Users, 
  ArrowRight, ArrowLeft, Check, Sparkles,
  MessageSquare, FileText, Palette, Zap,
  Upload, Bot, Rocket
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Choose Your Kit", icon: Store },
  { id: 2, title: "Name & Describe", icon: Bot },
  { id: 3, title: "Brand Voice", icon: MessageSquare },
  { id: 4, title: "Customize", icon: Palette },
  { id: 5, title: "Launch", icon: Rocket }
];

const KIT_ICONS = {
  small_business: Store,
  online_seller: ShoppingCart,
  freelancer: Briefcase,
  consultant: Users
};

export default function AgentBuilderWizard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [kits, setKits] = useState({});
  const [brandVoices, setBrandVoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createdAgent, setCreatedAgent] = useState(null);
  
  // Form state
  const [config, setConfig] = useState({
    business_type: '',
    name: '',
    business_description: '',
    industry: '',
    brand_voice: 'professional',
    primary_color: '#14b8a6',
    target_audience: ''
  });

  useEffect(() => {
    fetchKits();
  }, []);

  const fetchKits = async () => {
    try {
      const response = await axios.get(`${API}/agent-builder/kits`);
      setKits(response.data.kits || {});
      setBrandVoices(response.data.brand_voices || {});
    } catch (err) {
      console.error('Failed to fetch kits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/agent-builder/create`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedAgent(response.data);
      setStep(5);
    } catch (err) {
      console.error('Failed to create agent:', err);
      alert('Failed to create agent. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const nextStep = () => {
    if (step < 5) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return config.business_type !== '';
      case 2: return config.name !== '' && config.business_description !== '';
      case 3: return config.brand_voice !== '';
      case 4: return true;
      default: return true;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Bot className="w-16 h-16 text-teal-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Create Your Custom Agent</h2>
          <p className="text-zinc-400 mb-6">Sign in to build your personalized AI business assistant</p>
          <Button onClick={() => navigate('/auth')} className="bg-teal-500 hover:bg-teal-600">
            Sign In to Start
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {STEPS.map((s, index) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isComplete = step > s.id;
              
              return (
                <div key={s.id} className="flex items-center">
                  <div className={`flex flex-col items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isComplete ? 'bg-teal-500 text-white' :
                      isActive ? 'bg-teal-500/20 border-2 border-teal-500 text-teal-400' :
                      'bg-zinc-800 text-zinc-500'
                    }`}>
                      {isComplete ? <Check className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-2 hidden sm:block ${isActive ? 'text-teal-400' : 'text-zinc-500'}`}>
                      {s.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`h-0.5 w-8 sm:w-16 mx-2 ${isComplete ? 'bg-teal-500' : 'bg-zinc-800'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 1: Choose Kit */}
            {step === 1 && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Choose Your Starter Kit</h1>
                  <p className="text-zinc-400">Select the kit that best matches your business</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(kits).map(([key, kit]) => {
                    const Icon = KIT_ICONS[key] || Store;
                    const isSelected = config.business_type === key;
                    
                    return (
                      <motion.button
                        key={key}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setConfig({ ...config, business_type: key })}
                        className={`relative p-6 rounded-2xl text-left transition-all ${
                          isSelected 
                            ? 'bg-teal-500/20 border-2 border-teal-500' 
                            : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-4 right-4">
                            <Check className="w-6 h-6 text-teal-400" />
                          </div>
                        )}
                        
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                          isSelected ? 'bg-teal-500/30' : 'bg-zinc-800'
                        }`}>
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-teal-400' : 'text-zinc-400'}`} />
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-2">{kit.name}</h3>
                        <p className="text-zinc-400 text-sm mb-4">{kit.description}</p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {kit.features?.slice(0, 3).map((feature, i) => (
                            <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded">
                              {feature}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-teal-400 font-bold">${kit.price}/mo</span>
                          <span className="text-zinc-500 text-sm">{kit.templates?.length || 0} templates</span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Name & Describe */}
            {step === 2 && (
              <div className="max-w-xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Name Your Agent</h1>
                  <p className="text-zinc-400">Tell us about your business</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-zinc-300 mb-2">Agent Name *</label>
                    <Input
                      value={config.name}
                      onChange={(e) => setConfig({ ...config, name: e.target.value })}
                      placeholder="e.g., Sarah's Bakery Assistant"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                    <p className="text-zinc-500 text-sm mt-1">This is how your agent will introduce itself</p>
                  </div>

                  <div>
                    <label className="block text-zinc-300 mb-2">Business Description *</label>
                    <textarea
                      value={config.business_description}
                      onChange={(e) => setConfig({ ...config, business_description: e.target.value })}
                      placeholder="Describe what your business does, who you serve, and what makes you unique..."
                      rows={4}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 mb-2">Industry (Optional)</label>
                    <Input
                      value={config.industry}
                      onChange={(e) => setConfig({ ...config, industry: e.target.value })}
                      placeholder="e.g., Food & Beverage, Fashion, Technology"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 mb-2">Target Audience (Optional)</label>
                    <Input
                      value={config.target_audience}
                      onChange={(e) => setConfig({ ...config, target_audience: e.target.value })}
                      placeholder="e.g., Young professionals, Parents, Small business owners"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Brand Voice */}
            {step === 3 && (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Choose Your Brand Voice</h1>
                  <p className="text-zinc-400">How should your agent communicate?</p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(brandVoices).map(([key, voice]) => {
                    const isSelected = config.brand_voice === key;
                    
                    return (
                      <motion.button
                        key={key}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setConfig({ ...config, brand_voice: key })}
                        className={`p-4 rounded-xl text-left transition-all ${
                          isSelected 
                            ? 'bg-teal-500/20 border-2 border-teal-500' 
                            : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-white">{voice.name}</h3>
                          {isSelected && <Check className="w-5 h-5 text-teal-400" />}
                        </div>
                        <p className="text-zinc-400 text-sm mb-2">{voice.description}</p>
                        <p className="text-zinc-500 text-xs italic">"{voice.tone}"</p>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Customize */}
            {step === 4 && (
              <div className="max-w-xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Customize Your Agent</h1>
                  <p className="text-zinc-400">Final touches before launch</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-zinc-300 mb-2">Brand Color</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={config.primary_color}
                        onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                        className="w-16 h-16 rounded-lg cursor-pointer border-0"
                      />
                      <div className="flex-1">
                        <Input
                          value={config.primary_color}
                          onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                          className="bg-zinc-900 border-zinc-700 text-white font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Summary Preview */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4">Agent Preview</h3>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Name:</span>
                        <span className="text-white">{config.name || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Kit:</span>
                        <span className="text-white">{kits[config.business_type]?.name || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Voice:</span>
                        <span className="text-white capitalize">{config.brand_voice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Templates:</span>
                        <span className="text-white">{kits[config.business_type]?.templates?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Monthly:</span>
                        <span className="text-teal-400 font-bold">${kits[config.business_type]?.price || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Launch */}
            {step === 5 && createdAgent && (
              <div className="max-w-xl mx-auto text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-24 h-24 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Sparkles className="w-12 h-12 text-teal-400" />
                </motion.div>

                <h1 className="text-3xl font-bold text-white mb-2">Your Agent is Ready!</h1>
                <p className="text-zinc-400 mb-8">
                  "{createdAgent.name}" has been created. Next, train your chatbot with your business documents.
                </p>

                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8 text-left">
                  <h3 className="text-white font-semibold mb-4">Next Steps:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-400 text-sm">1</span>
                      </div>
                      <div>
                        <p className="text-white">Upload Documents</p>
                        <p className="text-zinc-500 text-sm">Train your chatbot with FAQs, product info, policies</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-400 text-sm">2</span>
                      </div>
                      <div>
                        <p className="text-white">Use Your Templates</p>
                        <p className="text-zinc-500 text-sm">Generate content with your brand voice</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-400 text-sm">3</span>
                      </div>
                      <div>
                        <p className="text-white">Deploy Your Chatbot</p>
                        <p className="text-zinc-500 text-sm">Embed on your website or share the link</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/my-agents')}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    Go to My Agents
                  </Button>
                  <Button
                    onClick={() => navigate('/chatbot-builder')}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Train Chatbot Now
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        {step < 5 && (
          <div className="flex justify-between mt-12 max-w-2xl mx-auto">
            <Button
              onClick={prevStep}
              disabled={step === 1}
              variant="outline"
              className="border-zinc-700 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={!canProceed() || creating}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50"
              >
                {creating ? (
                  <>Creating...</>
                ) : (
                  <>
                    <Rocket className="w-4 h-4 mr-2" />
                    Create My Agent
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
